import { ArrowLeft, Globe, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { PageShell } from '@/components/layout/PageShell'
import { ReportMarkdown } from '@/components/research/ReportMarkdown'
import { Section } from '@/components/search/detail/Section'
import { Button } from '@/components/ui/button'
import { useOsintInvestigation } from '@/features/osint/hooks/use-osint'
import { OsintClarificationCard } from '@/features/osint/OsintClarificationCard'
import { OsintExportButton } from '@/features/osint/OsintExportButton'
import { OsintGraphCard } from '@/features/osint/OsintGraphCard'
import { OsintProgressPanel } from '@/features/osint/OsintProgressPanel'
import { OsintSourcesCard } from '@/features/osint/OsintSourcesCard'
import { useCaseContext } from '@/pages/cases/CaseContext'

export default function CaseOsintDetailPage() {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const { osintId } = useParams<{ osintId: string }>()
  const { data: investigation, isLoading } = useOsintInvestigation(caseId, osintId)
  // PageShell drops the summary of a pinned header below md, so there the brief
  // opens the scrolling content instead — unless it only repeats the title.
  const brief = investigation?.brief
  const briefRepeatsTitle = brief?.trim() === investigation?.title.trim()

  // Report [S#] citation → scroll to + flash the matching source row.
  const [sourceFlash, setSourceFlash] = useState<number | null>(null)
  useEffect(() => {
    if (sourceFlash == null) return
    const id = setTimeout(() => setSourceFlash(null), 2200)
    return () => clearTimeout(id)
  }, [sourceFlash])

  const run = investigation?.latestRun ?? null
  const isRunning = run?.status === 'running' || run?.status === 'queued'
  const failed = run?.status === 'finished_error'
  const awaitingInput = run?.status === 'awaiting_input'
  const clarification = run?.clarification ?? null
  const reportMd = run?.reportMd ?? null
  const sources = run?.sources ?? []
  const proposal = run?.graphProposal ?? null

  // The finished report + the entities it proposes — rendered once, then placed
  // either as the main column of the dossier grid (with a sources rail) or on
  // its own.
  const reportSection = reportMd ? (
    <div className="flex min-w-0 flex-col gap-6">
      <Section title={t('osint.report')} hint={t('osint.reportHint')}>
        <ReportMarkdown
          markdown={reportMd}
          findingCount={0}
          sourceCount={sources.length}
          onOpenSource={i => setSourceFlash(i - 1)}
        />
      </Section>
      {osintId && investigation && (
        <OsintGraphCard
          caseId={caseId}
          osintId={osintId}
          proposal={proposal}
          subject={{ title: investigation.title, brief: investigation.brief }}
        />
      )}
    </div>
  ) : null

  return (
    <PageShell
      layout="full"
      icon={Globe}
      title={investigation?.title ?? t('nav.subviews.osint')}
      summary={brief}
    >
      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-10 md:px-6">
        <div className="flex w-full flex-col gap-6">
          {brief && !briefRepeatsTitle && (
            <p className="-mb-3 text-sm text-muted-foreground md:hidden">{brief}</p>
          )}
          <div className="flex items-center justify-between gap-2">
            <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit px-2">
              <Link to={`/cases/${caseId}/osint`}>
                <ArrowLeft className="size-4" />
                {t('osint.backToList')}
              </Link>
            </Button>
            {caseId && osintId && (
              <OsintExportButton
                caseId={caseId}
                osintId={osintId}
                disabled={!investigation?.latestRun?.reportMd}
              />
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : !investigation ? (
            <p className="text-sm text-muted-foreground">{t('osint.notFound')}</p>
          ) : (
            <>
              {/* Live register checklist while the run is in progress. */}
              {isRunning && osintId && (
                <OsintProgressPanel caseId={caseId} osintId={osintId} startedAt={run?.startedAt} />
              )}

              {failed && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  {run?.errorMessage || t('osint.failed')}
                </div>
              )}

              {/* Agent paused for a missing identifier — collect answers and resume. */}
              {awaitingInput && clarification && osintId && (
                <OsintClarificationCard
                  key={`clar-${clarification.rounds}`}
                  caseId={caseId}
                  osintId={osintId}
                  clarification={clarification}
                />
              )}

              {reportSection ? (
                sources.length > 0 ? (
                  // Dossier layout: report fills the page width, sources dock
                  // as a sticky evidence rail on wide screens (stacked below on
                  // narrow ones). [S#] clicks still scroll + flash the rail.
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
                    {reportSection}
                    <aside className="self-start xl:sticky xl:top-6">
                      <OsintSourcesCard sources={sources} highlightIndex={sourceFlash} />
                    </aside>
                  </div>
                ) : (
                  reportSection
                )
              ) : sources.length > 0 ? (
                // No report, but sources streamed in (e.g. a failed run that
                // still cited some) — show them full width.
                <OsintSourcesCard sources={sources} highlightIndex={sourceFlash} />
              ) : (
                !isRunning &&
                !failed &&
                !awaitingInput && (
                  <p className="text-sm italic text-muted-foreground">{t('osint.noReport')}</p>
                )
              )}
            </>
          )}
        </div>
      </div>
    </PageShell>
  )
}
